/**
 * Keyboard + mouse with pointer lock. Sim-step oriented: `pressed`/`released` sets
 * accumulate between steps and are cleared by endStep(). Mouse deltas accumulate likewise.
 */
export class Input {
  readonly down = new Set<string>();
  readonly pressed = new Set<string>();
  readonly released = new Set<string>();
  mouseDx = 0;
  mouseDy = 0;
  wheel = 0;
  readonly buttons = new Set<number>();
  readonly buttonPressed = new Set<number>();
  locked = false;
  enabled = true;
  /** When a bot drives the game it writes into `virtual` and sets `useVirtual`. */
  useVirtual = false;
  readonly virtual = { down: new Set<string>(), pressed: new Set<string>(), mouseDx: 0, mouseDy: 0, buttonPressed: new Set<number>(), buttons: new Set<number>() };
  private readonly unbinders: (() => void)[] = [];

  constructor(private readonly target: HTMLElement) {
    const on = <K extends keyof WindowEventMap>(el: Window | HTMLElement | Document, ev: K | string, fn: (e: any) => void, opts?: AddEventListenerOptions) => {
      el.addEventListener(ev, fn as EventListener, opts);
      this.unbinders.push(() => el.removeEventListener(ev, fn as EventListener, opts));
    };
    on(window, 'keydown', (e: KeyboardEvent) => {
      if (!this.enabled) return;
      const k = e.code;
      if (!this.down.has(k)) this.pressed.add(k);
      this.down.add(k);
      if (k === 'Tab' || k === 'Space' || (k.startsWith('Arrow'))) e.preventDefault();
    });
    on(window, 'keyup', (e: KeyboardEvent) => { this.down.delete(e.code); this.released.add(e.code); });
    on(window, 'blur', () => { this.down.clear(); this.buttons.clear(); });
    on(target, 'mousemove', (e: MouseEvent) => {
      if (!this.locked) return;
      this.mouseDx += e.movementX; this.mouseDy += e.movementY;
    });
    on(target, 'mousedown', (e: MouseEvent) => {
      if (!this.enabled) return;
      if (!this.buttons.has(e.button)) this.buttonPressed.add(e.button);
      this.buttons.add(e.button);
    });
    on(window, 'mouseup', (e: MouseEvent) => { this.buttons.delete(e.button); });
    on(target, 'wheel', (e: WheelEvent) => { this.wheel += Math.sign(e.deltaY); }, { passive: true });
    on(document, 'pointerlockchange', () => { this.locked = document.pointerLockElement === target; });
    on(target, 'contextmenu', (e: Event) => e.preventDefault());
  }

  requestLock(): void {
    if (!this.locked) void this.target.requestPointerLock?.();
  }
  releaseLock(): void {
    if (this.locked) document.exitPointerLock();
  }

  isDown(code: string): boolean { return this.useVirtual ? this.virtual.down.has(code) : this.down.has(code); }
  wasPressed(code: string): boolean { return this.useVirtual ? this.virtual.pressed.has(code) : this.pressed.has(code); }
  isButton(b: number): boolean { return this.useVirtual ? this.virtual.buttons.has(b) : this.buttons.has(b); }
  wasButtonPressed(b: number): boolean { return this.useVirtual ? this.virtual.buttonPressed.has(b) : this.buttonPressed.has(b); }
  /** Consume accumulated mouse delta. */
  takeMouse(): [number, number] {
    if (this.useVirtual) {
      const r: [number, number] = [this.virtual.mouseDx, this.virtual.mouseDy];
      this.virtual.mouseDx = 0; this.virtual.mouseDy = 0;
      return r;
    }
    const r: [number, number] = [this.mouseDx, this.mouseDy];
    this.mouseDx = 0; this.mouseDy = 0;
    return r;
  }
  /** WASD (or left stick) axis in [-1,1]: x = right, y = forward. */
  moveAxis(): [number, number] {
    let x = 0, y = 0;
    if (this.isDown('KeyW') || this.isDown('ArrowUp')) y += 1;
    if (this.isDown('KeyS') || this.isDown('ArrowDown')) y -= 1;
    if (this.isDown('KeyD') || this.isDown('ArrowRight')) x += 1;
    if (this.isDown('KeyA') || this.isDown('ArrowLeft')) x -= 1;
    if (x === 0 && y === 0 && !this.useVirtual) { x = this.pad.moveX; y = this.pad.moveY; }
    return [x, y];
  }

  /** Gamepad state, polled once per sim step (§3.1 optional). Buttons are mapped onto key codes. */
  readonly pad = { connected: false, moveX: 0, moveY: 0, lookX: 0, lookY: 0 };
  private padPrev = new Set<string>();
  pollGamepad(): void {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [];
    const gp = pads && pads[0];
    this.pad.connected = !!gp;
    if (!gp) { this.pad.moveX = this.pad.moveY = this.pad.lookX = this.pad.lookY = 0; return; }
    const dz = (v: number) => (Math.abs(v) < 0.18 ? 0 : v);
    this.pad.moveX = dz(gp.axes[0] ?? 0);
    this.pad.moveY = -dz(gp.axes[1] ?? 0);
    this.pad.lookX = dz(gp.axes[2] ?? 0);
    this.pad.lookY = dz(gp.axes[3] ?? 0);
    // A=dodge, X=interact, Y=use, B/RT=attack, RB/LB=run, Start=pause
    const map: [number, string][] = [[0, 'Space'], [2, 'KeyE'], [3, 'KeyF'], [1, 'KeyJ'], [7, 'KeyJ'], [5, 'ShiftLeft'], [4, 'ShiftLeft'], [9, 'Escape']];
    const now = new Set<string>();
    for (const [i, code] of map) if (gp.buttons[i]?.pressed) now.add(code);
    for (const code of now) { if (!this.padPrev.has(code)) this.pressed.add(code); this.down.add(code); }
    for (const code of this.padPrev) if (!now.has(code)) { this.down.delete(code); this.released.add(code); }
    this.padPrev = now;
    // right stick → mouse delta
    this.mouseDx += this.pad.lookX * 14;
    this.mouseDy += this.pad.lookY * 14;
  }

  /** Call at the end of each sim step. */
  endStep(): void {
    this.pressed.clear(); this.released.clear(); this.buttonPressed.clear(); this.wheel = 0;
    this.virtual.pressed.clear(); this.virtual.buttonPressed.clear();
  }
  dispose(): void { for (const u of this.unbinders) u(); }
}
